import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Stack, Typography } from '@mui/material';
import DeviceSelector from './DeviceSelector';
import SoundPlayer from './SoundPlayer';
import {
  setMicrophoneDeviceId,
  setSpeakerDeviceId,
  setVideoDeviceId,
} from '../store/slices/deviceSlice';
import usePermissionRequest from '../hooks/usePermissionRequest';
import useDevicePermissions from '../hooks/useDevicePermissions';
import MicIndicator from './MicIndicator';
import CameraButton from './CameraButton';

const DeviceHeader = ({ device, t }) => {
  return (
    <Typography sx={{ fontSize: '14px', fontWeight: 'bold' }}>
      {t(device)}
    </Typography>
  );
};

const OptedNoDevice = ({ device, t }) => {
  const renderText = () => {
    switch (device) {
      case 'camera':
        return <>{t('You have opted not to use a camera.')}</>;
      case 'microphone':
        return <>{t('You have opted not to use a microphone.')}</>;
      case 'speaker':
        return <>{t('You have opted not to use a speaker.')}</>;
      default:
        return null;
    }
  };
  return <Typography sx={{ fontSize: '14px' }}>{renderText()}</Typography>;
};

function Devices({ streamHandler }) {
  const { t } = useTranslation();

  const {
    audiooutputDevices,
    audioinputDevices,
    videoinputDevices,
    microphoneDeviceId,
    speakerDeviceId,
    videoDeviceId,
  } = useSelector((state) => state.device);

  const [checkedPermission, setCheckedPermission] = useState({
    video: false,
    audio: false,
  });

  const dispatch = useDispatch();

  usePermissionRequest(streamHandler, checkedPermission, setCheckedPermission);
  useDevicePermissions(streamHandler, checkedPermission);

  const onSelectVideoinput = (deviceId) => {
    dispatch(setVideoDeviceId(deviceId));
  };

  const onSelectAudioinput = (deviceId) => {
    dispatch(setMicrophoneDeviceId(deviceId));
  };

  const onSelectAudiooutput = (deviceId) => {
    dispatch(setSpeakerDeviceId(deviceId));
  };

  return (
    <Stack spacing={0} alignItems="center" justifyContent="center">
      <Stack spacing={0} sx={{ width: '100%' }}>
        <DeviceHeader device="Camera" t={t} />
        {videoinputDevices.length > 0 ? (
          <Stack
            spacing={1}
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <DeviceSelector
              devices={videoinputDevices}
              preferredDevice={videoDeviceId}
              onSelect={onSelectVideoinput}
              label={t('Camera')}
            />
            <CameraButton />
          </Stack>
        ) : (
          <OptedNoDevice device="camera" t={t} />
        )}
      </Stack>

      <Stack spacing={0} sx={{ width: '100%' }}>
        <DeviceHeader device="Microphone" t={t} />
        {audioinputDevices.length > 0 ? (
          <Stack
            spacing={1}
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <DeviceSelector
              devices={audioinputDevices}
              preferredDevice={microphoneDeviceId}
              onSelect={onSelectAudioinput}
              label={t('Microphone')}
            />
            <MicIndicator />
          </Stack>
        ) : (
          <OptedNoDevice device="microphone" t={t} />
        )}
      </Stack>

      <Stack spacing={0} sx={{ width: '100%' }}>
        <DeviceHeader device="Speaker" t={t} />
        {audiooutputDevices.length > 0 ? (
          <Stack
            spacing={1}
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
          >
            <DeviceSelector
              devices={audiooutputDevices}
              preferredDevice={speakerDeviceId}
              onSelect={onSelectAudiooutput}
              label={t('Speaker')}
            />
            <SoundPlayer />
          </Stack>
        ) : (
          <OptedNoDevice device="speaker" t={t} />
        )}
      </Stack>
    </Stack>
  );
}

export default Devices;
